<template>
  <div
    class="flex items-center w-full h-full relative outline outline-solid outline-secondary-500 rounded-lg overflow-hidden transition-all duration-200 flex-grow flex-col justify-center group/card"
    :class="[
      {
        '!outline-[3px] !outline-primary-600': selected || isHovered,
        'cursor-pointer': readonly,
        'hover:outline-1 hover:outline-primary-450': !readonly,
        'hover:outline-[3px] hover:outline-primary-600': readonly && !disabled,
      },
    ]"
    @mouseenter="!disabled && (isHovered = true)"
    @mouseleave="isHovered = false"
    @click.stop="readonly && !disabled && emit('select-item', item.id)">
    <!-- Actions -->
    <div
      v-if="!disabled && !readonly && allowDelete"
      class="absolute top-0 left-0 z-20 rounded-br-md !p-1 flex justify-center items-center transition-all duration-200 bg-white group/removebtn cursor-pointer !pb-[5px] !pr-[5px]"
      :class="[isHovered ? 'opacity-100' : 'opacity-0']"
      @click.stop="emit('remove-item', item.id)">
      <X
        :size="20"
        class="text-secondary-975 group-hover/removebtn:text-primary-600"/>
    </div>
    <div
      v-if="!disabled && !readonly"
      v-show="allowSelect"
      class="rounded-bl-md p-1 absolute top-0 right-0 z-20 bg-white cursor-pointer flex items-center justify-center transition-all duration-200 !p-[6px] !pb-[5px] !pr-[5px]"
      @click="emit('select-item', item.id)">
      <Checkbox
        v-model="checkboxValue"
        binary
        :pt="{
          root: ({ props, context }) => ({
            class: ['!w-5 !h-5'],
          }),
          box: ({ props, context }) => ({
            class: [
              '!w-5 !h-5',
              // Colors
              {
                '!bg-primary': context.checked,
              },
              {
                '[&>svg]:text-white [&>svg]:w-[1rem] [&>svg]:h-[1rem]':
                  context.checked,
              },
              'hover:!border-primary-600',
            ],
          }),
        }"
        :pt-options="{ mergeSections: false, mergeProps: true }"
        :disabled="exceededMaxSelections && !selected"
        @click.stop>
        <template #icon="{ checked }">
          <Check v-if="checked" :stroke-width="4" :size="16" />
        </template>
      </Checkbox>
    </div>

    <div
      class="relative h-full w-full flex flex-col justify-center p-1 bg-white transition-all duration-200"
      :class="{
        'flex flex-col-reverse': format === 'image-text',
      }">
      <div
        class="absolute top-1 left-1 w-[calc(100%-0.5rem)] h-[calc(100%-0.5rem)] bg-primary-200 transition-all duration-200 rounded-md"
        :class="{
          '!h-0': (!selected && !isHovered) || (disabled && !selected),
        }"></div>
      <div
        v-if="format.includes('text')"
        class="flex justify-center items-center min-h-[62px] rounded-md z-[1]"
        :class="{
          '!min-h-[105px]': !format.includes('image'),
          'rounded-t-none': format === 'image-text',
          'rounded-b-none': format === 'text-image',
        }">
        <p
          v-if="!item.title && readonly"
          class="text-red-500 text-sm py-2 w-full items-center flex justify-center">
          {{ 'חסר טקסט' }}
        </p>
        <HeadLine
          v-else
          :id="item.id"
          v-model="titleEdited"
          :text="item.title"
          visual-boundary="none"
          :closable="false"
          :placeholder="textPlaceholderLabel"
          :editable="!disabled && !readonly"
          class="px-5.5 w-full"
          :class="{
            '!py-2': format === 'text',
          }"
          :options="headlineOptions"
          @update="handleTitleChange"/>
      </div>
      <div
        v-if="format.includes('image')"
        :id="triggerId"
        ref="buttonRef"
        class="flex flex-1 justify-center overflow-hidden rounded-md cursor-pointer z-[1]"
        :class="[
          {
            'rounded-b-none': format === 'image-text',
            'rounded-t-none': format === 'text-image',
          },
        ]"
        :style="{ 'aspect-ratio': '1.7/1', 'max-height': '210px' }"
        @click="() => emit('on-select-image', buttonRef)">
        <div v-if="item.image" class="w-full h-full relative group/imageedit">
          <div
            v-if="!disabled && !readonly"
            class="w-full h-full opacity-0 group-hover/imageedit:opacity-100 transition-opacity duration-300 absolute top-0 left-0 flex justify-center items-center bg-[#0006] z-10">
            <SquarePen class="text-white" :size="20" />
          </div>
          <img
            :src="item.image"
            alt="Item"
            class="w-full h-full object-cover transition-all duration-200"
            :class="{ 'opacity-70': selected || isHovered }"/>
        </div>
        <div v-else class="w-full h-full bg-secondary-75">
          <div
            v-if="!disabled"
            class="flex flex-col w-full h-full gap-1 items-center justify-center">
            <FolderUp
              :class="!readonly ? 'text-secondary-975' : 'text-red-500'"
              :size="20"/>
            <p class="text-secondary-975 text-sm">
              {{
                !readonly
                  ? t('blocksSettings.image_upload.add_image')
                  : t('blocksSettings.image_upload.image_missing')
              }}
            </p>
            <p class="text-secondary-500 text-sm">
              {{
                !readonly
                  ? t('blocksSettings.image_upload.max_size')
                  : t('blocksSettings.image_upload.need_to_upload_image')
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Check, FolderUp, SquarePen, X } from 'lucide-vue-next';
import Checkbox from 'primevue/checkbox';
import { type PropType, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { HeadLine } from '@amit/components';
import { ChoiceOptionType } from '@amit/types/src/blocks/choice';

const { t } = useI18n();

const props = defineProps({
  item: {
    type: Object as PropType<ChoiceOptionType>,
    required: true,
  },
  selected: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  readonly: {
    type: Boolean,
    required: false,
  },
  format: {
    type: String as PropType<'text' | 'image' | 'text-image' | 'image-text'>,
    default: 'image-text',
  },
  textPlaceholderLabel: {
    type: String,
    default: 'פה כדאי לשים טקסט',
  },
  triggerId: {
    type: String,
    default: '',
  },
  allowDelete: {
    type: Boolean,
    default: true,
  },
  allowSelect: {
    type: Boolean,
    default: true,
  },
  exceededMaxSelections: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'select-item',
  'remove-item',
  'on-select-image',
  'edit-item-text',
]);

const isHovered = ref(false);
const buttonRef = ref<HTMLDivElement>();
const titleEdited = ref(props.item.title ?? '');
// Handle selection state changes
const handleSelection = () => {
  // Don't allow selection if max selections are exceeded and this item isn't already selected
  if (props.exceededMaxSelections && !props.selected) return;
  emit('select-item', props.item.id);
};

const checkboxValue = computed({
  get: () => props.selected,
  set: () => handleSelection(),
});

const handleTitleChange = () => {
  if (titleEdited.value !== props.item.title) {
    emit('edit-item-text', titleEdited.value);
  }
};

const headlineOptions = computed(() => {
  const baseOptions = {
    defaultNode: 'headline' as 'headline',
    textAlign: 'center',
    fontSize: '14px',
    sizeBreakpoint: 0,
    maxLines:
      props.format.includes('image') && props.format.includes('text')
        ? 2
        : null,
  };

  return {
    ...baseOptions,
  };
});
</script>
